import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline'
import { Form, Formik } from 'formik'
import { useParams } from 'react-router-dom'
import { array, boolean, lazy, object, string } from 'yup'

import { Button } from '@/components/Button'
import Label from '@/components/Label'
import ResponseTypeRadioCards, { responseTypeOptions } from '@/components/Segments/Builder/ResponseTypeRadioCards'
import { Popover } from '@/components/TailwindUI/Popover'
import useFeatures from '@/components/useFeatures'
import { CONSOLE_WORKSHOP_RESPONSE_TYPE, WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { Feature } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import AppConfigInstallFormField from './AppConfigInstallFormField'

const formSchema = () => {
  return object().shape({
    features: array().of(
      object().shape({
        config: array().of(
          lazy((config: any) => {
            if (config.formInputType === 'file-upload') {
              return object().shape({
                defaultValue: object().shape({
                  name: string().required('An attachment is required.')
                })
              })
            } else {
              return object().shape({
                defaultValue: string()
                  .when('required', {
                    is: required => required,
                    then: (schema: any) => schema.required('Value is required')
                  })
                  .when('formInputType', {
                    is: formInputType => formInputType === 'url',
                    then: (schema: any) => schema.url('Value must be a valid URL')
                  })
                  .when('formInputType', {
                    is: formInputType => formInputType === 'email',
                    then: (schema: any) => schema.email('Value must be a valid email')
                  })
              })
            }
          })
        ),
        isQueryable: boolean(),
        responseType: string().oneOf(['verbatim', 'rag'])
      })
    )
  })
}

const AppConfigInstallForm = ({
  isStep = false,
  features,
  readOnly,
  onCancel,
  onSubmit
}: {
  isStep?: boolean
  features: Feature[]
  readOnly: boolean
  onCancel: () => void
  onSubmit: (features: any) => void
}) => {
  const initialValues = {
    features: features
  }
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  const { [CONSOLE_WORKSHOP_RESPONSE_TYPE]: showResponseType } = useFeatures()
  const { spaceId } = useParams<{
    spaceId: string
  }>()
  return (
    <Formik
      enableReinitialize
      validationSchema={formSchema}
      onSubmit={values => onSubmit(values)}
      initialValues={initialValues}
    >
      {({ values, setFieldValue }) => (
        <Form noValidate>
          {!isStep && (
            <div className="flex">
              <AdjustmentsHorizontalIcon className="h-[30px] w-[30px] text-blue-700" aria-hidden="true" />
              <div className="ml-3">
                <h3 className="text-lg font-bold leading-6 text-black">
                  {agentsFlagOn ? 'Agent' : 'App'} Configuration
                </h3>
                <p className="text-sm leading-6 text-zinc-500">Feature configuration settings</p>
              </div>
            </div>
          )}
          <div className="mt-6">
            {values.features
              ?.filter(
                feature =>
                  feature.config?.some(config => config.isAppParameter) || (showResponseType && feature.responseType)
              )
              .map(feature => {
                return (
                  <div key={feature.id} className="mt-6 space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pl-10 pb-6 sm:grid-cols-2 md:grid-cols-4">
                      {feature.responseType ? (
                        <div>
                          <h2 className="text-sm leading-7 text-zinc-500">Response</h2>
                          <p className="block text-base font-bold leading-6 text-gray-900">
                            Choose how responses from this content are returned
                          </p>
                          <div className="flex">
                            <Label className="text-sm font-normal text-brand-400">{"What's the difference?"}</Label>

                            <Popover
                              background="brand"
                              tooltipContent={
                                <div className="relative grid gap-4 text-white">
                                  <span className="text-sm text-brand-100">Response Types</span>
                                  {responseTypeOptions.map(option => (
                                    <div key={option.key} className="flex items-start">
                                      <div>
                                        <p className="text-sm font-black">{option.title}</p>
                                        <p className="text-xs">{option.tooltip}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h2 className="text-sm leading-7 text-zinc-500">Feature</h2>
                          <p className="block text-base font-bold leading-6 text-gray-900">{feature.name}</p>
                        </div>
                      )}

                      <div className="mr-2 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3 md:col-span-3">
                        <div className="sm:col-span-5">
                          {feature.config
                            ? feature.config
                                .filter(param => param.isAppParameter)
                                .map((config, configIndex) => {
                                  return (
                                    <div className="mb-3" key={`${feature.id}-${configIndex}`}>
                                      <AppConfigInstallFormField
                                        formikName={`features[${features.findIndex(
                                          appFeatures => appFeatures.id === feature.id
                                        )}].config[${feature.config?.findIndex(
                                          featureConfig => featureConfig.id === config.id
                                        )}]`}
                                        config={config}
                                        spaceId={spaceId}
                                      />
                                    </div>
                                  )
                                })
                            : null}
                          {showResponseType && feature.responseType && (
                            <ResponseTypeRadioCards
                              formatProps={{
                                name: `features[${features.findIndex(
                                  appFeatures => appFeatures.id === feature.id
                                )}].responseType`,
                                includeFormatting: false,
                                required: true
                              }}
                              value={feature.responseType || 'rag'}
                              onChange={(value: string) =>
                                setFieldValue(
                                  `features[${features.findIndex(
                                    appFeatures => appFeatures.id === feature.id
                                  )}].responseType`,
                                  value
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>
          <div className="mt-12 flex items-center justify-end gap-x-6">
            <Button color="secondary" disabled={readOnly} onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button color="primary" disabled={readOnly}>
              Save
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default AppConfigInstallForm
